import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { Image } from '@ahafood/ui/components/image';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { Badge, Star, Plus, Sparkle } from 'lucide-react';
import { useMemo, useCallback } from 'react';
import {
  getPersonalizeFn,
  type Item as PersonalizeItem,
} from '@/api/use-get-personalize';
import { useGoToOrderWebstore } from '@/api/use-webstore-order';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { useRouter } from '@/i18n/routing';
import { formatDistanceMeterKilometer } from '@/lib/eta-calculator';
import { trackEvent } from '@/lib/event-tracking';
import type { MenuItem } from '@/types/item';

export function NewYearPersonalizeItemCard({
  location,
  item,
  isAuthenticated,
  setShowLoginDialog,
}: {
  location?: string;
  item: PersonalizeItem;
  isAuthenticated: boolean;
  setShowLoginDialog: (show: boolean) => void;
}) {
  const goToOrderWebstore = useGoToOrderWebstore();
  const routes = useCityRoutes();
  const router = useRouter();
  const isClosed = item.status !== 'ACTIVE';

  const trackClickEvent = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'for_you_card_click',
      button_location: location ?? 'for_you_zone',
    });
  };

  const { showRating, rawRating, showSold, soldDisplay, imageSrc } =
    useMemo(() => {
      const sold = item.total_sold;
      const showSold = sold > 10;
      const soldDisplay =
        sold > 999 ? `${(sold / 1000).toFixed(1)}K` : sold.toString();
      const rating =
        item.rating?.average ||
        (item.rating?.google_average >= 4 ? item.rating.google_average : null);

      const imageSrc =
        item.image_thumb_url ||
        `${assetPrefix}/static/images/empty-store-bot.webp`;

      return {
        showRating: !!rating,
        rawRating: rating,
        showSold,
        soldDisplay,
        imageSrc,
      };
    }, [item]);

  const handleOrderNow = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      trackEvent('click_button', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: 'for_you_add_to_cart',
        button_location: location ?? 'for_you_zone',
      });
      if (isClosed) return;
      if (!isAuthenticated) return setShowLoginDialog(true);

      const convertedItem: MenuItem = {
        _id: item._id,
        name: item.name,
        price: item.price,
        image_thumb_url: item.image_thumb_url,
        discounted_price: item.discount_price,
        is_ai_image: item.is_ai_image ?? false,
      };

      try {
        await goToOrderWebstore(
          item.store_id.toString(),
          item.slug ?? '',
          '',
          convertedItem,
          item.store_id?.toString() || '',
          'for_you'
        );
        router.push(routes.storeDetail(item.slug ?? ''));
      } catch (error) {
        console.error('Failed to go to order webstore:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isAuthenticated,
      isClosed,
      goToOrderWebstore,
      item,
      router,
      routes,
      setShowLoginDialog,
    ]
  );

  const handleCardClick = useCallback(() => {
    if (!isAuthenticated) return setShowLoginDialog(true);
    trackClickEvent();
    if (!isClosed) router.push(routes.storeDetail(item.slug ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isClosed,
    router,
    routes,
    setShowLoginDialog,
    item.slug,
  ]);

  return (
    <Card
      onClick={handleCardClick}
      className="p-3 pb-5 bg-[#fff5df] cursor-pointer relative overflow-hidden rounded-3xl border-none shadow-md"
      //   style={{
      //     background:
      //       'linear-gradient(180deg, #fef5e2 0%, #fef3e0 25%, #F47B3A 50%, #ffc694 75%, #fff0db 100%)',
      //   }}
    >
      {isClosed && (
        <div className="absolute inset-0 bg-neutral-50/50 z-20 rounded-2xl">
          <Badge className="absolute bg-neutral-90 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1.5 shadow-md font-bold">
            Tạm ngưng
          </Badge>
        </div>
      )}

      <div className="flex gap-3">
        {/* Content */}
        <div className="flex-1 flex flex-col justify-center min-w-0 pl-2">
          <div>
            <h3 className="font-semibold text-base text-neutral-90 line-clamp-2 mb-1">
              {item.name}
            </h3>

            {/* Store info */}
            <div className="flex items-center gap-1 text-xs text-neutral-60 mb-2">
              <span>{formatDistanceMeterKilometer(item.distance * 1000)}</span>
              <span className="truncate">• {item.page_name}</span>
            </div>

            {/* Rating & Sold */}
            {(showRating || showSold) && (
              <div className="flex items-center gap-3 text-xs text-neutral-60 mb-2">
                {showRating && (
                  <span className="flex items-center gap-1">
                    {item.rating?.total !== 0 ? (
                      <Star className="size-3 fill-[#FEBC2F] text-[#FEBC2F]" />
                    ) : (
                      <img
                        src={`${assetPrefix}/static/images/gg-rating.webp`}
                        alt="Rating"
                        className="h-3 w-auto"
                      />
                    )}
                    <span className="font-bold">{rawRating!.toFixed(1)}</span>
                  </span>
                )}
                {showSold && (
                  <span className="flex items-center gap-1">
                    <img
                      src={`${assetPrefix}/static/icons/bag.svg`}
                      alt="sold"
                      className="size-3"
                    />
                    {soldDisplay} lượt bán
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-2">
            <span className="font-bold text-lg text-primary">
              {formatCurrency(item.discount_price ?? item.price)}
            </span>
            {item.discount_price && item.discount_price < item.price && (
              <span className="text-xs text-neutral-40 line-through">
                {formatCurrency(item.price)}
              </span>
            )}
          </div>
        </div>

        {/* Image & Button */}
        <div className="relative flex-1 shrink-0">
          <div className="relative w-full aspect-square rounded-2xl overflow-hidden border-2 border-yellow-30">
            <Image
              src={imageSrc}
              alt={item.name}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 50vw, 128px"
            />
            {item.is_ai_image && (
              <Badge className="absolute top-2 right-2 size-6 bg-white/50 hover:bg-white/90 border-none shadow-sm p-1">
                <Image
                  fill
                  className="p-0.5"
                  src={`${assetPrefix}/static/icons/white-ai.svg`}
                  alt="AI"
                />
              </Badge>
            )}
          </div>
          <Button
            onClick={handleOrderNow}
            disabled={isClosed}
            className="absolute -bottom-2 right-1/2 translate-x-1/2 p-0 bg-gradient-to-b from-[#f46737] to-[#ed431b] text-white font-bold shadow-lg hover:bg-[#ed431b] z-10 w-4/5 border border-red-50 rounded-full"
          >
            <div className="size-full relative px-6 py-2 pt-2.5 h-9">
              Thêm
              <Sparkle className="absolute top-2.5 right-3 text-[#fdd534]" />
            </div>
          </Button>
        </div>
      </div>

      {/* AI suggestion badge */}
      {/* {item. && (
        <div className="mt-3 pt-3 border-t border-dashed border-neutral-30 flex items-start gap-2 text-xs text-neutral-70">
          <img
            src={`${assetPrefix}/static/icons/sparkle.svg`}
            alt="reason"
            className="size-4 mt-0.5"
          />
          <p className="line-clamp-2">{item.reason}</p>
        </div>
      )} */}
    </Card>
  );
}
